import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Wifi, Plus, Trash2, RefreshCw, History, CheckCircle, XCircle, AlertCircle, Clock, Power, PowerOff } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import AddSmartDeviceDialog from "./AddSmartDeviceDialog";
import {
  SmartDevice,
  useUpdateSmartDevice,
  useDeleteSmartDevice,
  useCheckConnection,
  useManualSaunaControl,
} from "@/hooks/useSmartDevices";
import { useSmartDeviceLogs } from "@/hooks/useSmartDeviceLogs";

interface SmartDevicesSectionProps {
  providerId: string;
  devices: SmartDevice[];
  devicesLoading: boolean;
  onDeviceAdded: () => void;
}

const SmartDevicesSection = ({
  providerId,
  devices,
  devicesLoading,
  onDeviceAdded,
}: SmartDevicesSectionProps) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<string | null>(null);
  const updateDevice = useUpdateSmartDevice();
  const deleteDevice = useDeleteSmartDevice();
  const checkConnection = useCheckConnection();
  const manualControl = useManualSaunaControl();

  const handleToggleDevice = async (deviceId: string, currentEnabled: boolean) => {
    try {
      await updateDevice.mutateAsync({
        id: deviceId,
        is_enabled: !currentEnabled,
      });
    } catch (error) {
      console.error('Error toggling device:', error);
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    try {
      await deleteDevice.mutateAsync(deviceId);
      onDeviceAdded();
    } catch (error) {
      console.error('Error deleting device:', error);
    }
  };

  const handleCheckConnection = async (device: SmartDevice) => {
    await checkConnection.mutateAsync(device);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <CardTitle className="flex items-center space-x-2">
              <Wifi className="h-5 w-5" />
              <span>Sauna Control Devices</span>
            </CardTitle>
            <Button
              onClick={() => setShowAddDialog(true)}
              size="sm"
              className="w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Device
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {devicesLoading ? (
            <div className="grid grid-cols-1 gap-4">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-24 bg-muted/50 rounded animate-pulse"></div>
              ))}
            </div>
          ) : devices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Wifi className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No Sauna Control Devices configured yet.</p>
              <p className="text-sm mt-2">Click "Add Device" to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {devices.map((device) => (
                <div
                  key={device.id}
                  className="border rounded-lg p-4 space-y-3 hover:border-primary/50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-2 sm:space-y-0">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-semibold">{device.device_name}</h4>
                        <Badge
                          variant={device.is_enabled ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {device.is_enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Device ID: {device.device_id}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        API: https://api-{device.device_id}.wellsuites.co/
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Preheat:</span>
                        <Input
                          type="number"
                          min={5}
                          max={120}
                          value={device.preheat_minutes || 30}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 30;
                            updateDevice.mutate({ id: device.id, preheat_minutes: value });
                          }}
                          className="h-6 w-16 text-xs"
                        />
                        <span className="text-xs text-muted-foreground">mins</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-700 border-green-300 hover:bg-green-50"
                        onClick={() => manualControl.mutate({ device, action: 'on' })}
                        disabled={manualControl.isPending}
                      >
                        <Power className="h-4 w-4 mr-1" />
                        Turn On
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-700 border-red-300 hover:bg-red-50"
                        onClick={() => manualControl.mutate({ device, action: 'off' })}
                        disabled={manualControl.isPending}
                      >
                        <PowerOff className="h-4 w-4 mr-1" />
                        Turn Off
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCheckConnection(device)}
                        disabled={checkConnection.isPending}
                      >
                        <RefreshCw
                          className={`h-4 w-4 mr-2 ${
                            checkConnection.isPending ? "animate-spin" : ""
                          }`}
                        />
                        Check Connection
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setExpandedLogs(expandedLogs === device.id ? null : device.id)}
                      >
                        <History className="h-4 w-4 mr-2" />
                        Logs
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pt-2 border-t">
                    <div className="flex items-center space-x-2">
                      <Badge
                        variant={device.is_connected ? "default" : "destructive"}
                        className="text-xs"
                      >
                        {device.is_connected ? "Connected" : "Disconnected"}
                      </Badge>
                      {device.last_connection_check && (
                        <span className="text-xs text-muted-foreground">
                          Last checked: {new Date(device.last_connection_check).toLocaleString()}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm">
                          {device.is_enabled ? "Enabled" : "Disabled"}
                        </span>
                        <Switch
                          checked={device.is_enabled}
                          onCheckedChange={() =>
                            handleToggleDevice(device.id, device.is_enabled)
                          }
                        />
                      </div>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Device</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{device.device_name}"? This action
                              cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteDevice(device.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  {/* Device Logs Section */}
                  {expandedLogs === device.id && <DeviceLogsSection deviceId={device.id} />}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddSmartDeviceDialog
        providerId={providerId}
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onDeviceAdded={onDeviceAdded}
      />
    </>
  );
};

// Device Logs Component
const DeviceLogsSection = ({ deviceId }: { deviceId: string }) => {
  const { data: logs, isLoading } = useSmartDeviceLogs(deviceId);

  const getActionIcon = (actionType: string, success: boolean) => {
    if (actionType === 'skip_turn_off') {
      return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
    return success ? (
      <CheckCircle className="h-4 w-4 text-green-600" />
    ) : (
      <XCircle className="h-4 w-4 text-destructive" />
    );
  };

  const getActionLabel = (actionType: string) => {
    switch (actionType) {
      case 'turn_off':
        return 'Turn Off';
      case 'turn_on':
        return 'Turn On';
      case 'skip_turn_off':
        return 'Skipped Turn Off';
      default:
        return actionType;
    }
  };

  return (
    <div className="mt-4 border-t pt-4">
      <div className="flex items-center space-x-2 mb-3">
        <History className="h-4 w-4" />
        <h5 className="text-sm font-semibold">Activity Log</h5>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-muted/50 rounded animate-pulse"></div>
          ))}
        </div>
      ) : logs && logs.length > 0 ? (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {logs.map((log) => (
            <div
              key={log.id}
              className="border rounded p-3 space-y-2 text-sm bg-background/50"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2 flex-1">
                  {getActionIcon(log.action_type, log.success)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{getActionLabel(log.action_type)}</span>
                      {log.bookings && (
                        <Badge variant="outline" className="text-xs">
                          #{log.bookings.booking_number}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Badge variant={log.success ? "default" : "destructive"} className="text-xs">
                  {log.success ? "Success" : "Failed"}
                </Badge>
              </div>

              {log.next_booking_within_50min && (
                <p className="text-xs text-muted-foreground pl-6">
                  {log.response_message || "Next booking within 50 minutes"}
                </p>
              )}

              {log.error_message && (
                <p className="text-xs text-destructive pl-6">{log.error_message}</p>
              )}

              {log.response_status && (
                <p className="text-xs text-muted-foreground pl-6">
                  HTTP {log.response_status}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-4">
          No activity logs yet
        </p>
      )}
    </div>
  );
};

export default SmartDevicesSection;
